import { Lottie, LottieHandle } from 'lottie-react';
import { useRef, useState } from 'react';

import { Badge } from '@/core/lib/new-architecture/store/badges.store';

const toPercent = (value: number, max: number) => (max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0);

const ProgressBar: React.FC<{ percent: number; className: string }> = ({ percent, className }) => (
  <div className={`overflow-hidden rounded-10 bg-neutral-50 ${className}`}>
    <div className="h-full rounded-10 bg-lagon-700 transition-all" style={{ width: `${percent}%` }} />
  </div>
);

const BadgeCard: React.FC<{ badge: Badge; checkedClassName: string; userValue?: number; hasProgressBarOnCard?: boolean }> = ({ badge, checkedClassName, userValue, hasProgressBarOnCard }) => {
  const { title, subtitle, description, uncheckUrl, checkUrl, hoverUrl, isChecked, cat } = badge;

  const lottieRef = useRef<LottieHandle>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isAnimationReady, setIsAnimationReady] = useState(false);

  // un badge débloqué affiche son animation à l'arrêt sur la première image, et la joue au survol
  const hasAnimation = isChecked && !!hoverUrl;
  const showAnimation = hasAnimation && isAnimationReady;

  const onMouseEnter = () => {
    setIsHovered(true);
    lottieRef.current?.play();
  };
  // pas de pause brutale : `loop` repasse à false, le cycle en cours va au bout, puis `complete` ramène à la première image
  const onMouseLeave = () => setIsHovered(false);

  return (
    <div className={`space-y-3 rounded-3 border p-4 ${isChecked ? checkedClassName : 'border-stroke-secondary bg-neutral-0'}`} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <div className="relative mx-auto w-fit">
        {/* l'image reste dans le flux pour donner sa taille à la boîte, et visible tant que l'animation n'est pas prête */}
        <img src={isChecked ? checkUrl : uncheckUrl} className={`mx-auto ${showAnimation ? 'invisible' : ''}`} alt={title} />
        {hasAnimation && (
          <Lottie
            lottieRef={lottieRef}
            src={hoverUrl}
            className={`absolute inset-0 size-full ${showAnimation ? '' : 'invisible'}`}
            loop={isHovered}
            autoplay={false}
            subscriptions={{
              ready: () => setIsAnimationReady(true),
              // ne se déclenche qu'en fin de lecture sans boucle, donc uniquement après la sortie du survol
              complete: () => lottieRef.current?.stop(),
            }}
          />
        )}
      </div>
      <div>
        <div className="text-caption-primary font-medium">{title}</div>
        {subtitle && <div className={`text-caption-secondary ${isChecked ? 'text-neutral-200' : 'text-content-placeholder'}`}>{subtitle}</div>}
      </div>
      {hasProgressBarOnCard && !isChecked && <ProgressBar percent={toPercent(userValue ?? 0, badge.value)} className="h-1" />}
      <p className={`text-caption-secondary ${cat === 'co2' && isChecked ? 'text-[white]' : 'text-content-placeholder'}`}>{description}</p>
    </div>
  );
};

export default BadgeCard;
